import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import type { AhsPolicy } from '@/components/AhsRenewalDialog';
import { useApi } from '@/lib/legacy';
import type { PolicySummary } from '@/lib/policy';

/** The response of GET /policies/{id} as returned, typed by what the page and the AHS renewal dialog read. */
export type PolicyRecord = PolicySummary & AhsPolicy;

/**
 * The loaded policy, and the page's one "now": the header and the Policy Details cards read the term
 * against it, so they never disagree. It is the time the policy loaded, so the term reads the same until
 * a reload.
 */
export type PolicyDetails = { policy: PolicyRecord | null; loading: boolean; failed: boolean; now: DateTime };

/* Used only outside a provider: "now" is taken when it is read. */
const OUTSIDE_PROVIDER: PolicyDetails = {
  policy: null,
  loading: true,
  failed: false,
  get now() {
    return DateTime.now();
  },
};

const PolicyDetailsContext = createContext<PolicyDetails>(OUTSIDE_PROVIDER);

/**
 * Loads the route's policy with GET /policies/{id}: once per policy id, kept while the user moves
 * between sections. The new page loads nothing else. Holds the page's "now": taken on mount and again
 * when a policy loads for a new id. Nothing else refreshes it.
 */
export function PolicyDetailsProvider({ children }: { children: ReactNode }) {
  const { policyId } = useParams<{ policyId: string }>();
  const { getPolicyWithId } = useApi();
  // The id the loaded state belongs to. Until it matches the route's, the page is loading: the
  // first render under a new policy id never shows the previous policy.
  const [loaded, setLoaded] = useState<{ id: string; policy: PolicyRecord | null; failed: boolean } | null>(null);
  const [now, setNow] = useState(() => DateTime.now());

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const policy = (await getPolicyWithId(policyId)) as PolicyRecord | null | undefined;
        if (current) {
          setLoaded({ id: policyId, policy: policy || null, failed: !policy });
          setNow(DateTime.now());
        }
      } catch {
        if (current) {
          setLoaded({ id: policyId, policy: null, failed: true });
          setNow(DateTime.now());
        }
      }
    })();
    return () => {
      current = false;
    };
  }, [policyId, getPolicyWithId]);

  const value: PolicyDetails =
    loaded && loaded.id === policyId
      ? { policy: loaded.policy, loading: false, failed: loaded.failed, now }
      : { policy: null, loading: true, failed: false, now };
  return <PolicyDetailsContext.Provider value={value}>{children}</PolicyDetailsContext.Provider>;
}

export const usePolicyDetails = (): PolicyDetails => useContext(PolicyDetailsContext);
